import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Container,
  Popover,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  eachWeekOfInterval,
  startOfMonth,
  endOfMonth,
  format,
  isAfter,
  isValid,
} from "date-fns";
import { DatePicker } from "@mui/x-date-pickers";

export default function WeekPicker({
  today,
  onWeekChange,
  currentCompWeek,
  sendDataToParent,
  weeksWithData = [],
  monthsWithData = [],
}) {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentWeek);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // console.log("Months with data: ", monthsWithData);
  const { t } = useTranslation("statistics");

  const monthsWithDataSet = useMemo(
    () =>
      new Set(
        monthsWithData.map((m) =>
          m.length === 7 ? m : format(new Date(m), "yyyy-MM")
        )
      ),
    [monthsWithData]
  );
  const firstMonthWithData = useMemo(() => {
    if (!monthsWithData.length) return null;
    return new Date(`${[...monthsWithData].sort()[0]}-01T00:00:00`);
  }, [monthsWithData]);

  const lastMonthWithData = useMemo(() => {
    if (!monthsWithData.length) return null;
    return new Date(`${[...monthsWithData].sort().slice(-1)[0]}-01T00:00:00`);
  }, [monthsWithData]);

  const weekStarts = useMemo(
    () => startOfWeek(currentWeek, { weekStartsOn: 1 }),
    [currentWeek]
  );
  const weekEnds = useMemo(
    () => endOfWeek(currentWeek, { weekStartsOn: 1 }),
    [currentWeek]
  );

  const fromISO = (s) => new Date(`${s}T00:00:00`);

  const updateAvailableWeeks = (month) => {
    if (!isValid(month)) return;
    const allWeeks = eachWeekOfInterval(
      { start: startOfMonth(month), end: endOfMonth(month) },
      { weekStartsOn: 1 }
    ).filter((d) => !isAfter(d, today));

    const dataWeeks = allWeeks.filter((d) =>
      weeksWithDataSet.has(format(d, "yyyy-MM-dd"))
    );

    setAvailableWeeks(dataWeeks);
  };

  const updateSelectedMonthFromWeek = (week) => {
    setSelectedMonth(new Date(week));
  };

  const emitWeekChange = (weekDate) => {
    if (onWeekChange) onWeekChange(weekDate);
  };

  const setWeekEverywhere = (weekDate) => {
    const start = startOfWeek(weekDate, { weekStartsOn: 1 });
    setCurrentWeek(start);
    updateSelectedMonthFromWeek(start);
    updateAvailableWeeks(start);
    emitWeekChange(start);
  };

  const handleOpenPopOver = (e) => setAnchorEl(e.currentTarget);
  const handleClosePopOver = () => setAnchorEl(null);

  const handleWeekChange = (valueTs) => {
    const d = new Date(Number(valueTs));
    setWeekEverywhere(d);
    handleClosePopOver();
  };
  const handleMonthChange = (newMonth) => {
    if (!isValid(newMonth)) return;
    setSelectedMonth(newMonth);
    const allWeeks = eachWeekOfInterval(
      { start: startOfMonth(newMonth), end: endOfMonth(newMonth) },
      { weekStartsOn: 1 }
    ).filter((d) => !isAfter(d, today));
    const dataWeeks = allWeeks.filter((d) =>
      weeksWithDataSet.has(format(d, "yyyy-MM-dd"))
    );
    setAvailableWeeks(dataWeeks);
    if (dataWeeks.length > 0) {
      setWeekEverywhere(dataWeeks[dataWeeks.length - 1]);
    }
  };

  const latestWeekWithData = useMemo(() => {
    if (!weeksWithData || weeksWithData.length === 0) return null;
    const weekDates = weeksWithData
      .map((s) => startOfWeek(fromISO(s), { weekStartsOn: 1 }))
      .filter((d) => !isAfter(d, today));
    if (weekDates.length === 0) return null;
    return weekDates.reduce((acc, d) => (d > acc ? d : acc), weekDates[0]);
  }, [weeksWithData, today]);

  const atLatestWeekWithData = useMemo(() => {
    if (!latestWeekWithData) return false;
    return weekStarts.getTime() === latestWeekWithData.getTime();
  }, [latestWeekWithData, weekStarts]);

  const isNextWeekInFuture = isAfter(addWeeks(weekStarts, 1), today);
  const disableNext = latestWeekWithData
    ? atLatestWeekWithData
    : isNextWeekInFuture;

  const hasAnyData = weeksWithData && weeksWithData.length > 0;
  const disablePrev = !hasAnyData;

  const handleArrowBack = () => {
    if (disablePrev) return;
    const previousWeek = subWeeks(currentWeek, 1);
    setWeekEverywhere(previousWeek);
  };

  const handleArrowNext = () => {
    if (disableNext) return;
    const nextWeek = addWeeks(currentWeek, 1);
    setWeekEverywhere(nextWeek);
  };

  useEffect(() => {
    if (currentCompWeek) {
      setCurrentWeek(currentCompWeek);
      setSelectedMonth(currentCompWeek);
      updateAvailableWeeks(currentCompWeek);
    } else {
      setCurrentWeek(today);
      setSelectedMonth(today);
      updateAvailableWeeks(today);
    }
  }, [currentCompWeek]);

  useEffect(() => {
    if (typeof sendDataToParent === "function") {
      sendDataToParent(() => setWeekEverywhere(today));
    }
    updateAvailableWeeks(selectedMonth || today);
  }, []);
  useEffect(() => {
    if (!monthsWithData.length) return;
    const curKey = format(selectedMonth, "yyyy-MM");
    if (!monthsWithDataSet.has(curKey)) {
      const latest = new Date(
        `${[...monthsWithData].sort().slice(-1)[0]}-01T00:00:00`
      );
      setSelectedMonth(latest);
      updateAvailableWeeks(latest);
    }
  }, [monthsWithData, monthsWithDataSet]);

  useEffect(() => {
    if (!weeksWithData || weeksWithData.length === 0) return;

    const weekDates = weeksWithData
      .map((s) => startOfWeek(fromISO(s), { weekStartsOn: 1 }))
      .filter((d) => !isAfter(d, today));

    if (weekDates.length === 0) return;

    const latest = weekDates.reduce(
      (acc, d) => (d > acc ? d : acc),
      weekDates[0]
    );

    const currentIsInData = weeksWithData.includes(
      format(weekStarts, "yyyy-MM-dd")
    );

    if (!currentIsInData || isAfter(weekStarts, latest)) {
      setWeekEverywhere(latest);
    } else {
      updateAvailableWeeks(currentWeek);
      updateSelectedMonthFromWeek(currentWeek);
    }
  }, [weeksWithData]);

  const availableWeeksTs = useMemo(
    () => availableWeeks.map((d) => d.getTime()),
    [availableWeeks]
  );

  const selectValueTs = useMemo(() => {
    const ts = weekStarts.getTime();
    return availableWeeksTs.includes(ts) ? ts : "";
  }, [availableWeeksTs, weekStarts]);

  const weeksWithDataSet = useMemo(
    () => new Set(weeksWithData),
    [weeksWithData]
  );

  const shouldDisableMonth = (monthDate) => {
    const key = format(monthDate, "yyyy-MM");
    return !monthsWithDataSet.has(key);
  };

  return (
    <Container
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20px",
        gap: "20px",
      }}
    >
      <IconButton onClick={handleArrowBack} disabled={disablePrev}>
        <ArrowBack />
      </IconButton>

      <Typography
        variant="body"
        sx={{ cursor: "pointer", textAlign: { xs: "center" } }}
        onClick={handleOpenPopOver}
      >
        {t("weekPicker.weekOf", {
          start: format(weekStarts, "d MMM"),
          end: format(weekEnds, "d MMM yyyy"),
        })}
      </Typography>

      <Popover
        className="popover_fields"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClosePopOver}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        {...(isXs
          ? {
              anchorReference: "anchorPosition",
              anchorPosition: {
                top: 96,
                left: Math.round(window.innerWidth / 2),
              },
              transformOrigin: { vertical: "top", horizontal: "center" },
            }
          : {
              anchorEl,
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              transformOrigin: { vertical: "top", horizontal: "center" },
            })}
        sx={{
          "& .MuiFormLabel-root": { top: "9px" },
          "& .MuiInputBase-input": { padding: "20.5px 14px" },
        }}
      >
        <Box
          className="popover_box"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            // gap: 1,
            p: 1,
            width: "100%",
          }}
        >
          <DatePicker
            label="Select Week, Month and Year"
            value={selectedMonth}
            onChange={handleMonthChange}
            minDate={
              firstMonthWithData
                ? startOfMonth(firstMonthWithData)
                : new Date("2024-01-01")
            }
            maxDate={lastMonthWithData ? endOfMonth(lastMonthWithData) : today}
            views={["year", "month"]}
            slotProps={{ textField: { fullWidth: true, size: "small" } }}
            shouldDisableMonth={shouldDisableMonth}
          />

          <Select
            value={selectValueTs}
            onChange={(e) => handleWeekChange(e.target.value)}
            displayEmpty
            sx={{ minWidth: 260 }}
          >
            {availableWeeks.map((weekStartDate) => {
              const weekStartStr = format(weekStartDate, "yyyy-MM-dd");
              const isBold = weeksWithDataSet.has(weekStartStr);
              const valueTs = weekStartDate.getTime();

              return (
                <MenuItem
                  key={valueTs}
                  value={valueTs}
                  sx={{ fontWeight: "normal" }}
                >
                  {`Week of ${format(weekStartDate, "MMM d")} - ${format(
                    endOfWeek(weekStartDate, { weekStartsOn: 1 }),
                    "MMM d"
                  )}`}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
      </Popover>

      <IconButton onClick={handleArrowNext} disabled={disableNext}>
        <ArrowForward />
      </IconButton>
    </Container>
  );
}
